import { useState } from 'react';
import { Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { humanizeApiError } from '@/lib/apiError';
import { ContractMetadataForm } from './ContractMetadataForm';
import { getContractTypeSchema } from './contractTypeSchema';
import type { ContractDetail, UpdateContractPayload } from './types';

/**
 * ContractMetadataCard — FR-076
 *
 * Editable fields (human metadata):
 *   title, description, contract_type, vendor_name, customer_name,
 *   contract_value (Decimal string, NFR-008), currency, start_date,
 *   end_date, auto_renewal, notice_period_days, status
 *
 * Read-only AI fields (locked icon):
 *   contract_number, gate_decision, parties, clauses, compliance_findings
 */

interface ContractMetadataCardProps {
  contract: ContractDetail | null;
  isLoading: boolean;
  isEditing: boolean;
  onSaved: (updated: ContractDetail) => void;
  onCancelEdit: () => void;
}

function ReadOnlyField({
  label,
  value,
  locked = false,
}: {
  label: string;
  value: React.ReactNode;
  locked?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground flex items-center gap-1">
        {label}
        {locked && <Lock className="h-3 w-3" aria-label="AI-generated, read-only" />}
      </dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

export function ContractMetadataCard({
  contract,
  isLoading,
  isEditing,
  onSaved,
  onCancelEdit,
}: ContractMetadataCardProps) {
  const [form, setForm] = useState<UpdateContractPayload>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Populate form once when entering edit mode
  if (isEditing && Object.keys(form).length === 0 && contract) {
    setForm({
      title: contract.title,
      description: contract.description ?? '',
      contract_type: contract.contract_type,
      vendor_name: contract.vendor_name ?? '',
      customer_name: contract.customer_name ?? '',
      contract_value: contract.contract_value ?? '',
      currency: contract.currency,
      start_date: contract.start_date ?? '',
      end_date: contract.end_date ?? '',
      auto_renewal: contract.auto_renewal,
      notice_period_days: contract.notice_period_days ?? undefined,
      status: contract.status,
    });
  }

  async function handleSave() {
    if (!contract) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      // Drop cleared optional numeric/date fields — an empty string is not a
      // valid decimal/date, and omitting them means "leave unchanged".
      const payload: UpdateContractPayload = { ...form };
      for (const key of ['contract_value', 'start_date', 'end_date'] as const) {
        if (payload[key] === '') payload[key] = undefined;
      }
      const updated = await api.put<ContractDetail>(`/api/v1/contracts/${contract.id}`, payload);
      setForm({});
      onSaved(updated);
    } catch (err) {
      setSaveError(humanizeApiError(err, 'Failed to save changes. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setForm({});
    setSaveError(null);
    onCancelEdit();
  }

  if (isLoading || !contract) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-24" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const schema = getContractTypeSchema(contract.contract_type);
  const formattedValue = contract.contract_value
    ? `${contract.currency} ${Number(contract.contract_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Contract Details</CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <ContractMetadataForm
            form={form}
            isSaving={isSaving}
            saveError={saveError}
            contractStatus={contract.status}
            onChange={setForm}
            onSave={() => { void handleSave(); }}
            onCancel={handleCancel}
          />
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <ReadOnlyField label="Type" value={schema.label} />
            <ReadOnlyField label="Status" value={contract.status.replace('_', ' ')} />
            <ReadOnlyField label={schema.vendorLabel} value={contract.vendor_name} />
            <ReadOnlyField label={schema.customerLabel} value={contract.customer_name} />
            {schema.showValue && (
              <>
                <ReadOnlyField label={schema.valueLabel} value={formattedValue} />
                <ReadOnlyField label="Currency" value={contract.currency} />
              </>
            )}
            {schema.showTerm && (
              <>
                <ReadOnlyField label="Start Date" value={contract.start_date} />
                <ReadOnlyField label="End Date" value={contract.end_date} />
              </>
            )}
            {schema.showRenewal && (
              <ReadOnlyField
                label="Auto-Renewal"
                value={contract.auto_renewal
                  ? `Yes${contract.notice_period_days ? ` — ${contract.notice_period_days} day notice` : ''}`
                  : 'No'}
              />
            )}
            <ReadOnlyField label="Contract #" value={contract.contract_number} locked />
            <ReadOnlyField label="Gate Decision" value={contract.gate_decision ?? 'None'} locked />
            <ReadOnlyField label="Created" value={new Date(contract.created_at).toLocaleDateString()} />
            {contract.description && (
              <div className="col-span-2">
                <ReadOnlyField label="Notes" value={contract.description} />
              </div>
            )}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
